import React, { Fragment } from 'react';
import { Spinner } from '../';
import ReactTooltip from 'react-tooltip';
import {
  faFolderUpload,
  faCopy,
  faCut,
  faPaste,
  faArchive,
  faTimes,
  faFileUpload,
  faFolderPlus,
  faFolderOpen,
  faDownload,
} from '@fortawesome/pro-regular-svg-icons';
import {
  FileSystemStore,
  Stores,
  FileSystemEntry,
  fileSystemStore,
} from '../../store';
import { fileSystemActions, messageActions } from '../../action';
import {
  bytesToSize,
  lastPathSegment,
  downloadBlobURL,
  byteArraysToBlobURL,
  getParentFileSystemDirectory,
} from '../../util';
import { fsApi } from '../../api-layer';
import { List, ListItem } from '../list';
import { A11yButton, Button, ButtonGroup } from '../button';
import { PageContent } from '../page';
import styled from '../../styles/styled-components';
import * as makeSubject from 'callbag-subject';
import { pipe, forEach } from 'callbag-basics';
import { throttleWithBreakthrough } from '../../callbags';
import { OverlayPanel } from '../overlayPanel';
import { FileDownloads, areDownloadingFilesInProgress } from './fileDownloads';
import { InputWithConfirm } from '../input';
import { inject, observer } from 'mobx-react';
import { Glyphicon } from '../icons';
import { DataTable } from '../table/dataTable';
import { areFileUploadsInProgress, FileUploadComp } from './fileUploads';
import { theme } from '../../styles/theme';
import { FileActionButtons } from './fileActionsButtons';
import { TableCellProps, TableHeaderProps } from 'react-virtualized';
import 'react-virtualized/styles.css';
import { VirtualizedDataTable } from '../table/virtualizedDataTable';
import { Loader } from '../loader';

type FileListOwnProps = {};
type FileListStoreProps = { fileSystemStore: FileSystemStore };
type FileListProps = FileListOwnProps & FileListStoreProps;
type FileListState = {
  searchQuery?: string;
  searchResultCount?: number;
  downloadingFiles: Record<string, { name: string; percent: number }>;
  clipboardContents: string[];
  pasteOperation?: 'Copy' | 'Cut';
};

class FileDataTable extends DataTable<FileSystemEntry> {}

class FileListComp extends React.Component<FileListProps, FileListState> {
  upload: HTMLInputElement;
  folderUpload: HTMLInputElement;
  fileDownloading: string = '';
  fileList: HTMLDivElement;
  headerBar: HTMLDivElement;
  lastComplete: string = '';
  pasteTooltip: React.RefObject<ReactTooltip>;
  upFolderTooltip: React.RefObject<ReactTooltip>;
  progressSink;

  constructor(props, context) {
    super(props, context);
    this.state = {
      searchQuery: '',
      searchResultCount: 200,
      downloadingFiles: {},
      clipboardContents: [],
    };
    this.progressSink = makeSubject();
    this.pasteTooltip = React.createRef();
    this.upFolderTooltip = React.createRef();
  }

  componentDidMount() {
    this.updateState(this.props.fileSystemStore);
    fsApi.createFileTree('');

    pipe(
      this.progressSink,
      throttleWithBreakthrough(200, progress => progress.percent === 1),
      forEach(({ fileId, percent, filepath }) =>
        this.setState(state => ({
          downloadingFiles: {
            ...state.downloadingFiles,
            [fileId]: { name: lastPathSegment(filepath), percent: percent },
          },
        })),
      ),
    );
  }

  componentWillUnmount() {
    this.progressSink(2);
  }

  updateState = (state: FileSystemStore) => {
    let v = state.downloads.complete;
    if (v) {
      if (this.lastComplete != v.path) {
        downloadBlobURL(byteArraysToBlobURL([v.data]), lastPathSegment(v.path));
        fsApi.removeFile(lastPathSegment(v.path));
        setTimeout(() => fileSystemActions.removeDownload(v.path), 0);
        this.lastComplete = v.path;
      }
    } else {
      this.lastComplete = '';
    }
  };

  openFolder = (path: string) => {
    fileSystemActions.clearSearch();
    fileSystemStore.setIsLoadingFiles(true);
    fsApi.createFileTree(path);
  };

  refresh = (path: string) => {
    fileSystemActions.clearSearch();
    fileSystemStore.setIsLoadingFiles(true);
    if (path == '') {
      fsApi.createFileTree('');
    } else {
      fsApi.reloadFileTree(path);
    }
    //sendCommandAsync("createFileTree", path, fileSystemActions.reloadFileTree)
  };

  canTransferFiles = () => {
    if (!fileSystemStore.tree.rootFolder.name) {
      messageActions.msg('warning', 'Select a destination folder.');
      return false;
    }

    if (areDownloadingFilesInProgress(this.state.downloadingFiles)) {
      messageActions.msg('warning', 'File download in progress.');
      return false;
    }

    if (areFileUploadsInProgress(fileSystemStore.uploads)) {
      messageActions.msg('warning', 'File upload already in progress.');
      return false;
    }
    return true;
  };

  handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    // console.log(e.target.files)

    if (!this.canTransferFiles()) {
      return;
    }

    messageActions.message({ style: 'success', text: 'File upload started.' });

    var fileArray = new Array<File>();
    for (var i = 0; i < e.target.files.length; i++) {
      fileArray.push(e.target.files[i]);
    }
    this.uploadFiles(fileArray, 0, 'File');
  };

  handleFolderUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.canTransferFiles()) {
      return;
    }

    messageActions.message({
      style: 'success',
      text: 'Folder upload started.',
    });

    var fileArray = new Array<File>();
    for (var i = 0; i < e.target.files.length; i++) {
      fileArray.push(e.target.files[i]);
    }
    this.uploadFiles(fileArray, 0, 'Folder');
  };

  uploadFiles = (
    fileList: Array<File>,
    currentIndex: number,
    uploadType: 'Folder' | 'File',
  ) => {
    if (fileList.length == 0) {
      return;
    }

    if (fileList[currentIndex].size > 100 * 1024 * 1024) {
      messageActions.msg('warning', 'Max file size is 100MB.');
      if (++currentIndex < fileList.length) {
        this.uploadFiles(fileList, currentIndex, uploadType);
        return;
      }
    }

    var fullPath = fileSystemStore.tree.rootFolder.name + '\\';
    var relativePath = fileList[currentIndex]['webkitRelativePath'];
    if (relativePath) {
      // Replace forward slashes with backslashes.
      fullPath += relativePath.replace(/\//g, '\\');
    } else {
      fullPath += fileList[currentIndex].name;
    }

    fsApi.uploadFile(fullPath, fileList[currentIndex], () => {
      messageActions.clearMessages();
      messageActions.message({
        style: 'success',
        text: `File ${currentIndex + 1} of ${fileList.length} uploaded.`,
      });
      fileSystemActions.uploadProgress({
        path: fileList[currentIndex].name,
        total: 1,
        uploaded: 1,
      });
      this.refresh(fileSystemStore.tree.rootFolder.name);

      if (++currentIndex < fileList.length) {
        this.uploadFiles(fileList, currentIndex, uploadType);
      }
    });
  };

  handleRowClick = (
    e: React.MouseEvent<HTMLTableRowElement>,
    foldername: string,
  ) => {
    this.openFolder(foldername);
  };

  handleFolderButtonClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    foldername: string,
  ) => {
    e.stopPropagation();
    this.openFolder(foldername);
  };

  download = (path: string) => {
    if (areDownloadingFilesInProgress(this.state.downloadingFiles)) {
      messageActions.msg('warning', 'File download already in progress.');
      return;
    }

    if (areFileUploadsInProgress(fileSystemStore.uploads)) {
      messageActions.msg('warning', 'File upload in progress.');
      return;
    }
    fsApi.requestFile(path, progress => this.progressSink(1, progress));
  };

  getNavigationButtons() {
    const tree = this.props.fileSystemStore.tree;

    return (
      <div className="borderless-toolbar" ref={ref => (this.headerBar = ref)}>
        <Button
          onClick={() => {
            fileSystemActions.goBack();
            this.refresh(fileSystemStore.tree.rootFolder.name);
          }}
          data-tip
          data-for="back-tooltip"
          icon="arrowLeft"
          aria-label="Go back"
          style={{ marginRight: '2px' }}
        />
        <ReactTooltip id="back-tooltip" place="top" effect="solid" type="dark">
          <span> Back </span>
        </ReactTooltip>
        <Button
          onClick={() => {
            fileSystemActions.goForward();
            this.refresh(fileSystemStore.tree.rootFolder.name);
          }}
          data-tip
          data-for="forward-tooltip"
          icon="arrowRight"
          aria-label="Go forward"
          style={{ marginRight: '10px' }}
        />
        <ReactTooltip
          id="forward-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> Forward </span>
        </ReactTooltip>
        <Button
          onClick={() => this.refresh(tree.rootFolder.name)}
          data-tip
          data-for="refresh-tooltip"
          icon="refreshCw"
          aria-label="Refresh"
          style={{ marginRight: '2px' }}
        />
        <ReactTooltip
          id="refresh-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> Refresh </span>
        </ReactTooltip>
        <Button
          onClick={() => {
            var parentFolder = getParentFileSystemDirectory(
              fileSystemStore.tree.rootFolder.name,
            );
            this.openFolder(parentFolder);
            if (!parentFolder) {
              this.upFolderTooltip.current.setState({ show: false });
            }
          }}
          data-tip
          data-for="upfolder-tooltip"
          icon="arrowUp"
          aria-label="Up"
          disabled={!fileSystemStore.tree.rootFolder.name}
        />
        <ReactTooltip
          id="upfolder-tooltip"
          place="top"
          effect="solid"
          type="dark"
          ref={this.upFolderTooltip}
        >
          <span>
            Up to{' '}
            {getParentFileSystemDirectory(
              fileSystemStore.tree.rootFolder.name,
            ) || 'Drive List'}
          </span>
        </ReactTooltip>

        <div className="search-file-bar-cont">
          <div className="file-bar">
            <InputWithConfirm
              value={tree.rootFolder.name}
              onConfirm={(_, value) => this.openFolder(value)}
              confirmButton="Open"
            />
          </div>
          <div className="search-bar">
            <InputWithConfirm
              onConfirm={(ev, text) => {
                if (
                  ev.nativeEvent instanceof MouseEvent &&
                  !!fileSystemStore.searchResult
                ) {
                  fileSystemActions.clearSearch();
                  fileSystemStore.setIsSearching(false);
                } else if (text == '') {
                  fileSystemActions.clearSearch();
                  fileSystemStore.setIsSearching(false);
                } else if (!fileSystemStore.tree.rootFolder.name) {
                  messageActions.msg(
                    'warning',
                    'You must select a starting folder.',
                  );
                } else {
                  fsApi.search(text, fileSystemStore.tree.rootFolder.name);
                  fileSystemStore.setIsSearching(true);
                }
              }}
              onEscape={() => {
                fileSystemActions.clearSearch();
                fileSystemStore.setIsSearching(false);
              }}
              placeholder="Search..."
              icon={!!fileSystemStore.searchResult ? 'x' : 'search'}
            />
          </div>
        </div>
      </div>
    );
  }

  getFileList() {
    let {
      fileSystemStore: { tree },
    } = this.props;

    if (!tree.rootFolderExists) {
      return <div>Directory does not exist</div>;
    }
    if (!tree.rootFolder.childFolders.length && !tree.rootFolder.files.length)
      return <div>Directory is empty</div>;

    return (
      <VirtualizedDataTable
        data={fileSystemStore.fileSystemEntries}
        initialSortDirection="ASC"
        initialSortKey="name"
        groupByKey="groupOrder"
        columnProps={[
          {
            label: (
              <input
                type="checkbox"
                style={{ height: '15px', width: '15px' }}
                onChange={changeEv => {
                  fileSystemStore.setAllSelected(
                    changeEv.currentTarget.checked,
                  );
                }}
              />
            ),
            disableSort: true,
            dataKey: 'isSelected',
            width: 25,
            minWidth: 25,
            cellRenderer: e => this.renderSelectCell(e),
          },
          {
            label: '',
            disableSort: true,
            dataKey: 'type',
            width: 25,
            minWidth: 25,
            cellRenderer: e => this.renderIconCell(e),
          },
          {
            label: 'Name',
            headerStyle: { cursor: 'pointer' },
            dataKey: 'name',
            width: 200,
            minWidth: 100,
            flexGrow: 1,
            cellRenderer: e => this.renderNameCell(e),
            headerRenderer: e => this.renderTableHeaderCell(e),
          },
          {
            label: 'Date Created',
            headerStyle: { cursor: 'pointer' },
            dataKey: 'dateCreated',
            width: 200,
            cellRenderer: e => this.renderDateCreatedCell(e),
          },
          {
            label: 'Date Modified',
            headerStyle: { cursor: 'pointer' },
            dataKey: 'dateModified',
            width: 200,
            cellRenderer: e => this.renderDateModifiedCell(e),
          },
          {
            label: 'Size',
            headerStyle: { cursor: 'pointer' },
            dataKey: 'size',
            width: 100,
            cellRenderer: e => this.renderSizeCell(e),
          },
          {
            label: '',
            dataKey: '',
            width: 175,
            minWidth: 175,
            cellRenderer: e => this.renderActionsCell(e),
          },
        ]}
      />
    );
  }

  getMainOperationButtons() {
    return (
      <ButtonGroup style={{ marginTop: '10px' }}>
        <Button
          onClick={() => {
            var result = prompt('Folder name:');
            if (result) {
              fsApi.createNewFolder(
                result,
                fileSystemStore.tree.rootFolder.name,
              );
            }
          }}
          data-tip
          data-for="newfolder-tooltip"
          faIconProps={{ icon: faFolderPlus, size: '1x' }}
          size="small"
          aria-label="New folder"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name
          }
          style={{ marginRight: '10px' }}
          content="New&nbsp;Folder"
        />
        <ReactTooltip
          id="newfolder-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> New Folder </span>
        </ReactTooltip>

        <Button
          onClick={() => {
            this.upload.value = '';
            this.upload.click();
          }}
          data-tip
          data-for="upload-tooltip"
          aria-label="Upload file"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name
          }
          content="File"
          size="small"
          faIconProps={{ icon: faFileUpload, size: '1x' }}
          style={{ marginRight: '10px' }}
        />
        <ReactTooltip
          id="upload-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> Upload&nbsp;File </span>
        </ReactTooltip>

        <Button
          onClick={() => {
            this.folderUpload.value = '';
            this.folderUpload.click();
          }}
          data-tip
          data-for="upload-folder-tooltip"
          aria-label="Upload folder"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name ||
            typeof document.createElement('input')['webkitdirectory'] !=
              'boolean'
          }
          content="Folder"
          size="small"
          faIconProps={{ icon: faFolderUpload, size: '1x' }}
          style={{ marginRight: '10px' }}
        />
        <ReactTooltip
          id="upload-folder-tooltip"
          place="top"
          effect="solid"
          type="dark"
        >
          <span> Upload&nbsp;Folder </span>
        </ReactTooltip>
      </ButtonGroup>
    );
  }

  getPrimaryBatchOperationButtons(selectedEntries: string[]) {
    return (
      <div
        style={{
          display: 'flex',
          borderLeft: '2px solid lightgray',
          paddingLeft: '10px',
          marginTop: '10px',
        }}
      >
        <Button
          onClick={e => {
            // Sometimes sticks open, so we'll hide it explicitly.
            this.pasteTooltip.current.setState({ show: false });

            fsApi.pasteFileSystemEntries(
              fileSystemStore.tree.rootFolder.name,
              this.state.pasteOperation,
              this.state.clipboardContents,
            );
            this.setState({
              clipboardContents: [],
              pasteOperation: null,
            });
          }}
          faIconProps={{
            icon: faPaste,
            size: '1x',
          }}
          size="small"
          data-tip
          data-for="paste-tooltip"
          aria-label="Paste"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name
          }
          style={{
            marginRight: '10px',
            whiteSpace: 'nowrap',
            display:
              (this.state.clipboardContents || []).length == 0
                ? 'none'
                : 'unset',
          }}
          content={
            <span key="paste-button">
              Paste
              {(this.state.clipboardContents || []).length == 0 ? (
                ''
              ) : (
                <span>
                  <Badge data-tip data-for="clipboard-contents">
                    {this.state.clipboardContents.length}
                  </Badge>
                  <ReactTooltip
                    id="clipboard-contents"
                    place="bottom"
                    effect="solid"
                    type="info"
                  >
                    <div style={{ textAlign: 'left' }}>
                      <p> {this.state.pasteOperation}: </p>
                      <div>
                        {this.state.clipboardContents.map(x => (
                          <p key={x}>{x}</p>
                        ))}
                      </div>
                    </div>
                  </ReactTooltip>
                </span>
              )}
            </span>
          }
        />
        <ReactTooltip
          id="paste-tooltip"
          place="top"
          effect="solid"
          type="dark"
          ref={this.pasteTooltip}
        >
          <span> Paste </span>
        </ReactTooltip>

        <Button
          onClick={e => {
            this.setState({
              clipboardContents: selectedEntries,
              pasteOperation: 'Copy',
            });
          }}
          faIconProps={{
            icon: faCopy,
            size: '1x',
          }}
          size="small"
          data-tip
          data-for="copy-tooltip"
          aria-label="Copy"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name
          }
          style={{
            marginRight: '10px',
            display: selectedEntries.length > 0 ? 'unset' : 'none',
          }}
          content="Copy"
        />
        <ReactTooltip id="copy-tooltip" place="top" effect="solid" type="dark">
          <span> Copy </span>
        </ReactTooltip>

        <Button
          onClick={e => {
            this.setState({
              clipboardContents: selectedEntries,
              pasteOperation: 'Cut',
            });
          }}
          faIconProps={{
            icon: faCut,
            size: '1x',
          }}
          size="small"
          data-tip
          data-for="cut-tooltip"
          aria-label="Cut"
          disabled={
            !!fileSystemStore.searchResult ||
            fileSystemStore.searching ||
            !fileSystemStore.tree.rootFolder.name
          }
          style={{
            marginRight: '10px',
            display: selectedEntries.length > 0 ? 'unset' : 'none',
          }}
          content="Cut"
        />
        <ReactTooltip id="cut-tooltip" place="top" effect="solid" type="dark">
          <span> Cut </span>
        </ReactTooltip>
      </div>
    );
  }
  getSecondaryBatchOperationButtons(selectedEntries: string[]) {
    if (selectedEntries.length == 0) {
      return null;
    }

    return (
      <div
        style={{
          display: 'flex',
          borderLeft: '2px solid lightgray',
          paddingLeft: '10px',
          marginTop: '10px',
        }}
      >
        <ButtonGroup>
          <Button
            onClick={e => {
              var result = prompt('Zip file name:');
              if (result) {
                fsApi.zipFileSystemEntries(
                  result,
                  selectedEntries,
                  fileSystemStore.tree.rootFolder.name,
                );
              }
            }}
            faIconProps={{ icon: faArchive, size: '1x' }}
            size="small"
            data-tip
            data-for="zip-tooltip"
            aria-label="Zip"
            disabled={
              !!fileSystemStore.searchResult ||
              fileSystemStore.searching ||
              !fileSystemStore.tree.rootFolder.name
            }
            style={{
              marginRight: '10px',
              display: selectedEntries.length > 0 ? 'unset' : 'none',
            }}
            content="Zip"
          />
          <ReactTooltip id="zip-tooltip" place="top" effect="solid" type="dark">
            <span> Zip </span>
          </ReactTooltip>

          <Button
            onClick={() => {
              var result = confirm(
                `Are you sure you want to delete these ${
                  selectedEntries.length
                } items?`,
              );
              if (result) {
                fsApi.deleteFileSystemEntries(
                  selectedEntries,
                  fileSystemStore.tree.rootFolder.name,
                );
              }
            }}
            faIconProps={{ icon: faTimes, size: '1x' }}
            size="small"
            data-tip
            data-for="delete-tooltip"
            aria-label="Delete"
            disabled={
              !!fileSystemStore.searchResult ||
              fileSystemStore.searching ||
              !fileSystemStore.tree.rootFolder.name
            }
            style={{
              display: selectedEntries.length > 0 ? 'unset' : 'none',
            }}
            content="Delete"
          />
          <ReactTooltip
            id="delete-tooltip"
            place="top"
            effect="solid"
            type="dark"
          >
            <span> Delete </span>
          </ReactTooltip>
        </ButtonGroup>
      </div>
    );
  }

  getSearchList() {
    const {
      fileSystemStore: { searchResult },
    } = this.props;
    const { searchResultCount } = this.state;
    if (!searchResult.success) {
      let details = '';
      if (searchResult.message.indexOf('scanning') !== -1) {
        details = 'Try again in a minute.';
      }
      return [
        <div>
          <p>Search failed: {searchResult.message}</p>
        </div>,
        <div>
          <p>{details}</p>
        </div>,
      ];
    }

    return (
      <SearchList divided>
        {searchResult.searchResults.slice(0, searchResultCount).map(result => (
          <ListItem
            key={result}
            icon="file"
            header={lastPathSegment(result)}
            description={result}
            showOnHover={
              <div>
                <Button
                  size="small"
                  data-tip
                  data-for="searchdownload-tooltip"
                  faIconProps={{ icon: faDownload, size: 'sm' }}
                  style={{ marginRight: '10px' }}
                  onClick={() => this.download(result)}
                />
                <ReactTooltip
                  id="searchdownload-tooltip"
                  place="top"
                  effect="solid"
                  type="dark"
                >
                  <span> Download&nbsp;File </span>
                </ReactTooltip>

                <Button
                  size="small"
                  data-tip
                  data-for="searchopen-tooltip"
                  faIconProps={{ icon: faFolderOpen, size: 'sm' }}
                  onClick={() => {
                    this.openFolder(getParentFileSystemDirectory(result));
                  }}
                />
                <ReactTooltip
                  id="searchopen-tooltip"
                  place="top"
                  effect="solid"
                  type="dark"
                >
                  <span> Open&nbsp;Folder </span>
                </ReactTooltip>
              </div>
            }
          />
        ))}
      </SearchList>
    );
  }

  render() {
    const { fileSystemStore } = this.props;
    const {
      fileSystemStore: { tree, searchResult, searching },
    } = this.props;

    if (!tree) {
      return (
        <div>
          <div className="col-xs-12" ref="fileList">
            loading files...
          </div>
        </div>
      );
    }

    var selectedEntries = fileSystemStore.fileSystemEntries
      .filter(x => x.isSelected)
      .map(x => x.name);

    const { downloadingFiles } = this.state;

    // This is to bypass the TypeScript error that "webkitdirectory"
    // property/attribute doesn't exist.
    var webkitAttribute = { webkitdirectory: 'true' };

    return (
      <div className="kabuto-live-panel">
        <input
          ref={ref => (this.upload = ref)}
          className="upload"
          type="file"
          multiple
          onChange={this.handleUpload}
        />
        <input
          ref={ref => (this.folderUpload = ref)}
          className="upload"
          type="file"
          {...webkitAttribute}
          multiple
          onChange={this.handleFolderUpload}
        />
        <div className="double-header">
          <div> File System </div>
        </div>

        {this.getNavigationButtons()}

        <div
          className="borderless-toolbar"
          style={{ flexWrap: 'wrap' }}
          ref={ref => (this.headerBar = ref)}
        >
          {this.getMainOperationButtons()}

          {this.getPrimaryBatchOperationButtons(selectedEntries)}

          {this.getSecondaryBatchOperationButtons(selectedEntries)}
        </div>

        <PageContent>
          {searching ? (
            <Spinner dark />
          ) : searchResult ? (
            this.getSearchList()
          ) : (
            this.getFileList()
          )}

          {fileSystemStore.isLoadingFiles && (
            <Loader text="Loading file system entries..." />
          )}

          <div style={{ textAlign: 'center', marginBottom: 30 }}>
            {searchResult ? (
              <Button
                onClick={() => {
                  this.setState({
                    searchResultCount: this.state.searchResultCount + 200,
                  });
                }}
                content="Load more..."
              />
            ) : null}
          </div>
        </PageContent>
        <OverlayPanel>
          {areDownloadingFilesInProgress(downloadingFiles) ? (
            <FileDownloads downloads={downloadingFiles} />
          ) : null}
        </OverlayPanel>
        <OverlayPanel>
          {areFileUploadsInProgress(fileSystemStore.uploads) ? (
            <FileUploadComp uploads={fileSystemStore.uploads} />
          ) : null}
        </OverlayPanel>
      </div>
    );
  }

  renderTableHeaderCell(cellProps: TableHeaderProps) {
    const { dataKey, label, disableSort, sortBy, sortDirection } = cellProps;

    return (
      <Fragment>
        {label || ''}
        {!disableSort &&
          dataKey == sortBy && (
            <Glyphicon
              style={{ verticalAlign: 'middle' }}
              glyph={sortDirection === 'ASC' ? 'arrowUp' : 'arrowDown'}
            />
          )}
      </Fragment>
    );
  }

  renderActionsCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return (
      fileSystemStore.tree.rootFolder.name && (
        <FileActionButtons
          downloadCallback={this.download}
          fileSystemEntry={entry}
        />
      )
    );
  }

  renderDateCreatedCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return entry.dateCreated
      ? new Date(entry.dateCreated).toLocaleString()
      : '';
  }

  renderDateModifiedCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return entry.dateModified
      ? new Date(entry.dateModified).toLocaleString()
      : '';
  }

  renderIconCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return entry && entry.type == 'Folder' ? (
      <FolderGlyphicon />
    ) : (
      <FileGlyphicon />
    );
  }

  renderNameCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    if (entry.type == 'Folder') {
      return (
        <A11yButton
          linkLook
          onClick={mouseEv => this.handleFolderButtonClick(mouseEv, entry.name)}
        >
          {lastPathSegment(entry.name)}
        </A11yButton>
      );
    }
    return lastPathSegment(entry.name);
  }

  renderSelectCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return (
      entry && (
        <input
          type="checkbox"
          checked={entry.isSelected || false}
          style={{ height: '15px', width: '15px' }}
          onChange={changeEv => {
            fileSystemStore.setEntryIsSelected(
              entry.type,
              entry.name,
              changeEv.currentTarget.checked,
            );
          }}
        />
      )
    );
  }

  renderSizeCell(cellProps: TableCellProps) {
    const entry = cellProps.rowData as FileSystemEntry;
    return entry.type == 'File' ? bytesToSize(entry.size) : '';
  }
}

export const FileList = inject<
  Stores,
  FileListOwnProps,
  FileListStoreProps,
  {}
>(({ fileSystemStore }) => ({ fileSystemStore }))(
  observer(FileListComp),
) as React.ComponentClass<FileListOwnProps>;

const FolderGlyphicon = () => (
  <Glyphicon style={{ color: '#4DADEA' }} glyph="folder" />
);

const FileGlyphicon = () => <Glyphicon glyph="file" />;

const SearchList = styled(List)`
  max-width: 700px;
`;

const Badge = styled('span')`
  background-color: ${theme.primaryColor};
  padding: 0.2em 0.4em;
  color: white;
  margin-left: 3px;
  border-radius: 2em;
  font-size: 0.9em;
`;
